import { AsyncPipe, isPlatformServer, NgComponentOutlet, ViewportScroller } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ExperimentalPendingTasks,
  inject,
  OnDestroy,
  PLATFORM_ID,
  QueryList,
  signal,
  ViewChildren,
  WritableSignal,
} from '@angular/core';
import { GenericLogger } from '@scalefast/ecommerce-core';
import { DynamicPageComponent } from 'src/app/core/classes/DynamicComponent';
import { BuilderPage } from 'src/app/core/interfaces';
import { BuilderPageComponentService } from 'src/app/shared/services/builder-page-component.service';
import { PAGE_DATA } from 'src/types/tokens/inject-tokens';

interface LazyLoadingComponentSettings {
  isVisible: boolean; // Visibility state of the component
  scrollOffset?: number; // Scroll offset in pixels
  timeoutId?: any; // Timeout reference for cleanup
}

@Component({
  selector: 'app-page-creator',
  templateUrl: './page-creator.component.html',
  styles: `
    :host {
      display: block;
    }
  `,
  standalone: true,
  imports: [NgComponentOutlet, AsyncPipe],
})
export class AppPageCreatorComponent implements OnDestroy {
  @ViewChildren(NgComponentOutlet) ngComponentOutlets!: QueryList<NgComponentOutlet>;

  private pageData: BuilderPage | null = inject(PAGE_DATA, { optional: true });
  components: Promise<DynamicPageComponent[]>;

  // Dictionary of components with their lazy loading settings
  lazyLoadingState: WritableSignal<Record<string, LazyLoadingComponentSettings>> = signal({});

  private scrollCheckInterval: any = null; // Reference to the interval timer
  private readonly CHECK_INTERVAL = 200; // Time in milliseconds to check the scroll position

  private changeDetector = inject(ChangeDetectorRef);
  private pendingTasks = inject(ExperimentalPendingTasks);
  private logger = inject(GenericLogger);
  private builderPageComponentService = inject(BuilderPageComponentService);
  #platformId = inject(PLATFORM_ID);
  #viewportScroller = inject(ViewportScroller);

  constructor() {
    const componentsReadyTask = this.pendingTasks.add();
    const pageCreatorComponents = this.pageData?.extraData?.components?.customComponents;
    if (pageCreatorComponents) {
      this.components = this.#createPageComponents(pageCreatorComponents)
        .then((components) => {
          this.changeDetector.detectChanges(); // TODO: Check if we can remove this detectChanges call
          this.logger.info('Page Creator component: Dynamic Components loaded');
          return components;
        })
        .catch((error) => {
          this.logger.error({ msg: 'Page Creator component: Error loading dynamic components', error });
          return [];
        })
        .finally(() => {
          this.logger.debug('Page Creator component: SSR dynamic components ready');
          componentsReadyTask();
        });
    } else {
      this.logger.info('Page Creator component: No page creator components found');
      this.components = Promise.resolve([]);
    }
  }

  ngOnDestroy(): void {
    this.#stopScrollCheck();
    this.#clearAllTimeouts();
  }

  /**
   * Returns a boolean indicating whether a dynamic component should be render
   *
   * @param component - The component whose visibility is being checked.
   * @returns boolean - `true` if the component is visible, `false` otherwise.
   */
  shouldComponentRender = (
    lazyLoadingState: Record<string, LazyLoadingComponentSettings>,
    component: DynamicPageComponent,
  ): boolean => {
    return lazyLoadingState[component.componentId]?.isVisible ?? false;
  };

  async #createPageComponents(pageCreatorComponents: any): Promise<DynamicPageComponent[]> {
    this.logger.info('Default component: Loading page creator components', pageCreatorComponents);
    const componentsPack = await Promise.all(
      pageCreatorComponents.map(async (componentMeta: any) => {
        const component = await this.builderPageComponentService.createPageComponent(componentMeta);
        this.#initComponentVisibility(component);

        return component;
      }),
    );
    return componentsPack.filter<DynamicPageComponent>((di): di is DynamicPageComponent => di !== null);
  }

  /**
   * Initializes the visibility state of a component and sets up lazy loading if enabled.
   * If lazy loading is activate, the component will be hidden until the specified scroll offset is reached or the timeout is reached.
   *
   * If the lazy loading is activate but not scroll offset or timeout is defined, the component will be hidden.
   * @param component The component to initialize.
   */
  #initComponentVisibility(component: DynamicPageComponent | null): void {
    if (isPlatformServer(this.#platformId) || !component?.lazyLoadingSettings?.activate) {
      return;
    }

    const componentId = component.componentId;
    if (!component.lazyLoadingSettings?.scrollOffset && !component.lazyLoadingSettings?.lazyTimeout) {
      this.logger.warn({
        msg: 'Lazy loading is activated but no scroll offset or timeout is defined for the component. The component will be displayed immediately. Check this configuration in Builder',
        component,
      });

      this.#updateComponentVisibility(componentId, { isVisible: true });
      return;
    }

    this.#updateComponentVisibility(componentId, { isVisible: false, scrollOffset: component.lazyLoadingSettings?.scrollOffset });

    // If has lazy timeout, set it up
    if (component.lazyLoadingSettings?.lazyTimeout) {
      const timeoutId = setTimeout(() => {
        this.#updateComponentVisibility(componentId, { isVisible: true });
      }, component.lazyLoadingSettings.lazyTimeout);
      this.#updateComponentVisibility(componentId, { timeoutId });
    }

    // If scroll offset is defined, set it up. Only one time in the component is necessary
    if (component.lazyLoadingSettings?.scrollOffset && !this.scrollCheckInterval) {
      this.#startScrollCheck();
    }
  }

  #startScrollCheck(): void {
    if (isPlatformServer(this.#platformId) || this.scrollCheckInterval) {
      return;
    }

    this.scrollCheckInterval = setInterval(() => {
      const scrollPosition = this.#viewportScroller.getScrollPosition()[1];
      Object.entries(this.lazyLoadingState()).forEach(([componentId, component]) => {
        if (component.scrollOffset && !component.isVisible && scrollPosition >= component.scrollOffset) {
          this.#updateComponentVisibility(componentId, { isVisible: true });
        }
      });

      if (this.#areAllComponentsVisible()) {
        this.#stopScrollCheck();
        this.logger.debug('Page Creator component: All components are visible');
      }
    }, this.CHECK_INTERVAL);

    this.logger.debug('Page Creator component: Started scroll position check interval');
  }

  #stopScrollCheck(): void {
    if (!this.scrollCheckInterval) {
      return;
    }

    clearInterval(this.scrollCheckInterval);
    this.scrollCheckInterval = null;
    this.logger.debug('Page Creator component: Stopped scroll position check interval');
  }

  #areAllComponentsVisible(): boolean {
    return Object.values(this.lazyLoadingState()).every((component) => component.isVisible);
  }

  #clearAllTimeouts(): void {
    Object.values(this.lazyLoadingState()).forEach((component) => {
      if (component.timeoutId) {
        clearTimeout(component.timeoutId);
      }
    });
  }

  /**
   * Updates the state of the lazy loading settings signal for a specific component.
   *
   * @param componentId - The unique identifier of the component to update.
   * @param updates - The partial updates to apply to the component settings.
   */
  #updateComponentVisibility(componentId: string, updates: Partial<LazyLoadingComponentSettings>): void {
    this.lazyLoadingState.update((settings) => ({
      ...settings,
      [componentId]: {
        ...settings[componentId],
        ...updates,
      },
    }));
  }
}
